import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Palette, Trash2 } from "lucide-react";
import { REPAIR_LABELS, REPAIR_TIERS, TOUCHUP_LABEL, repairRange, touchupRange } from "@/lib/pricing";
import { EXPAND } from "@/lib/motion";
import RepairPricePicker from "./RepairPricePicker";

const TIER_BTN = {
  none: "data-[on=true]:bg-slate-600 data-[on=true]:text-white",
  small: "data-[on=true]:bg-emerald-500 data-[on=true]:text-slate-950",
  medium: "data-[on=true]:bg-amber-500 data-[on=true]:text-slate-950",
  large: "data-[on=true]:bg-rose-500 data-[on=true]:text-white",
};
const tierCls = (tier) => `px-2.5 py-1 rounded-md text-[11px] font-semibold text-slate-400 transition-colors whitespace-nowrap ${TIER_BTN[tier]}`;

function RepairTiers({ panel, sel, settings, sym, onRepair, onRepairPrice }) {
  return (
    <div className="flex flex-wrap rounded-lg border border-slate-700 bg-slate-950/60 p-0.5" data-testid={`repair-tiers-${panel.key}`}>
      {REPAIR_TIERS.map((tier) => {
        const range = repairRange(settings, tier);
        if (!range) {
          return (
            <button key={tier} data-on={sel.repair === tier} onClick={() => onRepair(tier)} data-testid={`repair-tier-${panel.key}-${tier}`} className={tierCls(tier)}>
              {REPAIR_LABELS[tier].short}
            </button>
          );
        }
        const active = sel.repair === tier;
        return (
          <RepairPricePicker key={tier} panelKey={panel.key} tier={tier} range={range} sym={sym} active={active}
            value={active ? (sel.repairPrice ?? range.min) : range.min}
            onSelect={() => onRepair(tier, range.min)} onChange={onRepairPrice} className={tierCls(tier)} />
        );
      })}
    </div>
  );
}

function SprayModeTiers({ panel, sel, settings, sym, priceEach, onSprayMode, onTouchupPrice }) {
  const range = touchupRange(settings);
  const touch = sel.sprayMode === "touchup";
  return (
    <div className="flex flex-wrap rounded-lg border border-slate-700 bg-slate-950/60 p-0.5" data-testid={`spray-mode-${panel.key}`}>
      <button data-on={!touch} onClick={() => onSprayMode("full")} data-testid={`spray-mode-${panel.key}-full`}
        className="px-2.5 py-1 rounded-md text-[11px] font-semibold text-slate-400 transition-colors whitespace-nowrap data-[on=true]:bg-sky-500 data-[on=true]:text-slate-950">
        Full Respray<span className="font-mono-num opacity-80"> · {priceEach}</span>
      </button>
      <RepairPricePicker panelKey={panel.key} tier="touchup" range={range} sym={sym} active={touch} labelOverride={TOUCHUP_LABEL} testidPrefix="spray-mode"
        value={touch ? (sel.touchupPrice ?? range.min) : range.min}
        onSelect={() => onSprayMode("touchup", range.min)} onChange={onTouchupPrice}
        className="px-2.5 py-1 rounded-md text-[11px] font-semibold text-slate-400 transition-colors whitespace-nowrap data-[on=true]:bg-sky-500 data-[on=true]:text-slate-950" />
    </div>
  );
}

function PanelOptions({ panel, sel, settings, sym, priceEach, onSprayMode, onTouchupPrice, onRepair, onRepairPrice, onSpecial, onQty }) {
  return (
    <div className="pt-3 mt-3 border-t border-slate-800 flex flex-wrap items-center gap-2">
      <SprayModeTiers panel={panel} sel={sel} settings={settings} sym={sym} priceEach={priceEach} onSprayMode={onSprayMode} onTouchupPrice={onTouchupPrice} />
      <RepairTiers panel={panel} sel={sel} settings={settings} sym={sym} onRepair={onRepair} onRepairPrice={onRepairPrice} />
      {onQty && (
        <div className="flex rounded-lg border border-slate-700 bg-slate-950/60 p-0.5" data-testid={`qty-toggle-${panel.key}`}>
          {[1, 2].map((q) => (
            <button key={q} data-on={(sel.qty || 1) === q} onClick={() => onQty(q)} data-testid={`qty-${panel.key}-${q}`}
              className="px-2.5 py-1 rounded-md text-[11px] font-bold text-slate-400 transition-colors data-[on=true]:bg-amber-400 data-[on=true]:text-slate-950">
              ×{q} {q === 2 ? "Pair 一对" : "Single 单个"}
            </button>
          ))}
        </div>
      )}
      <button data-on={sel.special} onClick={() => onSpecial(!sel.special)} data-testid={`special-effect-toggle-${panel.key}`}
        className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold border border-slate-700 text-slate-400 transition-colors data-[on=true]:border-violet-400 data-[on=true]:bg-violet-500/15 data-[on=true]:text-violet-300">
        <Palette className="w-3.5 h-3.5" /> Tricoat 三层漆
      </button>
    </div>
  );
}

export default function PanelRow({ panel, sel, settings, sym, onToggle, onSprayMode, onTouchupPrice, onRepair, onRepairPrice, onSpecial, onQty, onRemove, priceEach }) {
  const pairHint = panel.per_unit && sel.selected && (sel.qty || 1) === 2 ? " · ×2" : "";
  return (
    <div data-on={sel.selected} className="rounded-xl border-2 border-slate-800 bg-slate-900/40 p-3 transition-all data-[on=true]:border-amber-500/50 data-[on=true]:bg-amber-500/[0.04]">
      <div className="flex items-center gap-3">
        <button onClick={onToggle} data-testid={`panel-checkbox-${panel.key}`}
          className={`w-6 h-6 shrink-0 rounded-md grid place-items-center border-2 transition-colors ${sel.selected ? "bg-amber-400 border-amber-400 text-slate-950" : "border-slate-600 hover:border-slate-400"}`}>
          {sel.selected && <Check className="w-4 h-4" strokeWidth={3} />}
        </button>
        <button onClick={onToggle} className="flex-1 text-left min-w-0">
          <div className="font-semibold text-sm truncate">{panel.en} {panel.zh && <span className="font-zh text-amber-300/70 text-xs">{panel.zh}</span>}{sel.selected && sel.sprayMode === "touchup" && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-sky-300 bg-sky-500/15 px-1.5 py-0.5 rounded">Touch-up</span>}</div>
          <div className="text-[11px] text-slate-500 font-mono-num">{priceEach} each{pairHint}</div>
        </button>
        {onRemove && (
          <button onClick={onRemove} data-testid={`custom-panel-remove-${panel.key}`} className="text-slate-500 hover:text-rose-400 p-1"><Trash2 className="w-4 h-4" /></button>
        )}
      </div>
      <AnimatePresence>
        {sel.selected && (
          <motion.div {...EXPAND} className="overflow-hidden">
            <PanelOptions panel={panel} sel={sel} settings={settings} sym={sym} priceEach={priceEach} onSprayMode={onSprayMode} onTouchupPrice={onTouchupPrice} onRepair={onRepair} onRepairPrice={onRepairPrice} onSpecial={onSpecial} onQty={onQty} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
